"use client"

import { getPageNameFromPath } from "@/app/(site)/_utils/getPageNameFromPath"
import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"

interface DepictCarouselTrackingScriptProps {
    carouselId: string
    carouselTitle: string
    productItems: ProductData[]
    locale: string
}

const DepictCarouselTrackingScript: React.FC<DepictCarouselTrackingScriptProps> = ({
    carouselId,
    carouselTitle,
    productItems
}) => {
    const siteContext = useSiteContext()
    const path = usePathname()
    const pageName = getPageNameFromPath(path)
    const isCurrentlyVisible = useRef(false)
    const hasTrackedWhileVisible = useRef(false)
    const sessionKey = useRef<string | null>(null)

    useEffect(() => {
        const carouselElement = document.getElementById(carouselId)

        if (!carouselElement || productItems.length === 0) return

        // Create a unique session key for this carousel on this page
        const currentSessionKey = `${path}|${carouselId}|${productItems.length}`

        // Reset tracking if session key changes (new carousel data or page change)
        if (sessionKey.current !== currentSessionKey) {
            hasTrackedWhileVisible.current = false
            sessionKey.current = currentSessionKey
        }

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    const wasVisible = isCurrentlyVisible.current
                    const isVisible = entry.isIntersecting

                    // Update visibility state
                    isCurrentlyVisible.current = isVisible

                    // Reset tracking state when carousel goes out of view
                    if (!isVisible && wasVisible) {
                        hasTrackedWhileVisible.current = false
                    }

                    // Track when carousel becomes visible and hasn't been tracked while visible
                    if (isVisible && !wasVisible && !hasTrackedWhileVisible.current) {
                        const listName = "carousel"
                        const listId = `${pageName}|${carouselTitle}`
                        const listHierarchy = `${pageName}|${carouselTitle}`

                        // Convert products to tracking format using the actual MinimizedCentraProduct objects
                        const trackingItems = productItems.map((product, index) => {
                            const trackingItem = adaptProductForTracking(product, {
                                item_list_name: listName,
                                item_list_id: listId,
                                index
                            })

                            // Extract currency from price string if not already present
                            const currency =
                                trackingItem.currency ||
                                (typeof product.price === "string"
                                    ? product.price.match(/[A-Z]{3,}$/)?.[0] || "EUR"
                                    : "EUR")

                            return {
                                ...trackingItem,
                                currency
                            }
                        })

                        import("@/lib/utils/tracking/gtmEvents").then(
                            ({ viewItemListEvent }) => {
                                viewItemListEvent(
                                    trackingItems,
                                    listName,
                                    listId,
                                    siteContext,
                                    "carousel",
                                    listHierarchy
                                )
                            }
                        )

                        // Mark as tracked while visible
                        hasTrackedWhileVisible.current = true
                    }
                })
            },
            { threshold: 0.1 }
        )

        observer.observe(carouselElement)

        return () => observer.disconnect()
    }, [carouselId, carouselTitle, productItems, pageName, siteContext, path])

    return null // This component doesn't render anything
}

export default DepictCarouselTrackingScript
